import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Brand } from "@/components/Brand";
import { LayoutDashboard, Users, Building2, MapPinned, FileText, Inbox, ClipboardList, LogOut, Menu, X, AlertOctagon, Truck, Footprints, UserCheck, MessageSquare, BookOpen, Palette, ClipboardCheck, Boxes, ShieldCheck, ListChecks, Database, CheckSquare, CalendarRange, Radar, Clock, Route, Calculator, Smartphone, History, ShoppingCart, UserCircle } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { PanicBanner } from "@/components/PanicBanner";
import { OperationalInbox } from "@/components/OperationalInbox";
import { api } from "@/lib/api";
import { toast } from "@/components/Toast";

const ITEMS = [
  { to: "/admin", end: true, label: "Inicio", icon: LayoutDashboard },
  { to: "/admin/alertas", label: "Alertas", icon: AlertOctagon, alert: true },
  { to: "/admin/clientes", label: "Clientes", icon: Building2 },
  { to: "/admin/usuarios", label: "Usuarios", icon: Users },
  { to: "/admin/rondas", label: "Rondas", icon: MapPinned },
  { to: "/admin/rondas-vivo", label: "Rondas en vivo", icon: Radar },
  { to: "/admin/novedades", label: "Novedades", icon: FileText },
  { to: "/admin/solicitudes", label: "Solicitudes", icon: Inbox },
  { to: "/admin/servicios", label: "Servicios", icon: ClipboardList },
  { to: "/admin/chats", label: "Chats", icon: MessageSquare },
  { to: "/admin/vehiculos", label: "Vehículos", icon: Truck },
  { to: "/admin/visitas", label: "Visitas", icon: Footprints },
  { to: "/admin/personal", label: "Personal Autorizado", icon: UserCheck },
  { to: "/admin/elementos", label: "Elementos de Trabajo", icon: ClipboardCheck },
  { to: "/admin/logistica", label: "Logística / Recursos", icon: Boxes },
  { to: "/admin/celulares", label: "Celulares Corporativos", icon: Smartphone },
  { to: "/admin/guardias", label: "Guardias Pasivas", icon: ShieldCheck },
  { to: "/admin/cronograma", label: "Cronograma", icon: CalendarRange },
  { to: "/admin/horas-extra", label: "Horas Extra", icon: Clock },
  // Fix A (2026-08-28): el cliente pidió que el módulo pase a llamarse "Supervisores".
  // La RUTA no cambia (/admin/recorridos) para no romper enlaces internos existentes.
  { to: "/admin/recorridos", label: "Supervisores", icon: Route },
  { to: "/admin/marcaciones", label: "Marcaciones de Supervisores", icon: History },
  { to: "/admin/cuestionarios", label: "Cuestionarios", icon: ListChecks },
  { to: "/admin/checklist-servicio", label: "Checklist de Servicio", icon: CheckSquare },
  { to: "/admin/manual", label: "Manual", icon: BookOpen },
  { to: "/admin/marca", label: "Marca", icon: Palette },
  { to: "/admin/migracion", label: "Migración / Respaldo", icon: Database },
  { to: "/admin/presupuesto", label: "Presupuesto", icon: Calculator },
  { to: "/admin/tareas-operativas", label: "Tareas Operativas", icon: ShoppingCart },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [pendingReq, setPendingReq] = useState(0);
  const [checklistAlerts, setChecklistAlerts] = useState(0);
  const lastUnread = useRef(0);
  const lastReq = useRef(0);
  const lastChecklist = useRef(0);
  const firstTick = useRef(true);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const [u, r, w] = await Promise.all([
          api.get("/chats/unread-count"),
          api.get("/internal-requests/pending-count"),
          api.get("/work-items/compliance-count"),
        ]);
        if (cancelled) return;
        const newUnread = u.data.count || 0;
        const newReq = r.data.count || 0;
        const newChecklist = w.data.total || 0;
        if (!firstTick.current) {
          if (newUnread > lastUnread.current) toast.info(`Nuevo mensaje en chats (${newUnread} sin leer)`);
          if (newReq > lastReq.current) toast.info(`Nueva solicitud pendiente (${newReq} en total)`);
          if (newChecklist > lastChecklist.current) toast.error(`Incumplimiento en Elementos de Trabajo (${newChecklist})`);
        }
        lastUnread.current = newUnread;
        lastReq.current = newReq;
        lastChecklist.current = newChecklist;
        firstTick.current = false;
        setUnread(newUnread);
        setPendingReq(newReq);
        setChecklistAlerts(newChecklist);
      } catch {/* silent */}
    };
    tick(); const i = setInterval(tick, 8000); return () => { cancelled = true; clearInterval(i); };
  }, []);

  const onLogout = async () => { await logout(); nav("/login"); };

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "var(--invict-bone)" }}>
      {/* Mobile top bar */}
      <div className="admin-mobile-topbar md:hidden">
        <Brand size="sm" iconOnly />
        <button onClick={() => setOpen(!open)} style={{ background: "none", border: "none", color: "#FFFBF4" }} data-testid="admin-menu-toggle">
          {open ? <X size={22}/> : <Menu size={22}/>}
        </button>
      </div>

      <aside
        style={{
          width: 240, background: "#0A0A0A", color: "#FFFBF4",
          position: "fixed", top: 0, bottom: 0, left: 0,
          display: "flex", flexDirection: "column",
          padding: "26px 0 18px",
          transform: open ? "translateX(0)" : undefined,
          zIndex: 60,
        }}
        className={`shrink-0 ${open ? "" : "max-md:-translate-x-full"} transition-transform`}
        data-testid="admin-sidebar"
      >
        <div style={{ padding: "0 22px 22px", borderBottom: "1px solid rgba(255,251,244,0.08)" }}>
          <Brand size="md" iconOnly />
        </div>
        <nav style={{ marginTop: 16, flex: 1, minHeight: 0, overflowY: "auto", display: "flex", flexDirection: "column", gap: 2 }}>
          {ITEMS.map(({ to, label, icon: Ic, end }) => {
            const showBadge = label === "Chats" && unread > 0;
            const showReqBadge = label === "Solicitudes" && pendingReq > 0;
            const showChecklistBadge = label === "Elementos de Trabajo" && checklistAlerts > 0;
            return (
              <NavLink
                key={to} to={to} end={end}
                onClick={() => setOpen(false)}
                className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
                data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <Ic size={16} /> {label}
                {showBadge && <span data-testid="badge-chats" style={{ marginLeft: "auto", background: "#BF3437", color: "#fff", borderRadius: 10, padding: "1px 7px", fontSize: 10, fontWeight: 800 }}>{unread}</span>}
                {showReqBadge && <span data-testid="badge-requests" style={{ marginLeft: "auto", background: "#BF3437", color: "#fff", borderRadius: 10, padding: "1px 7px", fontSize: 10, fontWeight: 800 }}>{pendingReq}</span>}
                {showChecklistBadge && <span data-testid="badge-checklist" style={{ marginLeft: "auto", background: "#BF3437", color: "#fff", borderRadius: 10, padding: "1px 7px", fontSize: 10, fontWeight: 800 }}>{checklistAlerts}</span>}
              </NavLink>
            );
          })}
        </nav>
        <div style={{ padding: "16px 18px", borderTop: "1px solid rgba(255,251,244,0.08)", flexShrink: 0 }}>
          <div style={{ fontSize: 11, color: "rgba(255,251,244,0.5)", letterSpacing: "0.1em", textTransform: "uppercase" }}>Sesión</div>
          <div style={{ fontSize: 13, color: "#FFFBF4", fontWeight: 700, marginTop: 4 }}>{user?.name}</div>
          <div style={{ fontSize: 11, color: "rgba(255,251,244,0.5)" }}>{user?.email}</div>
          <Link to="/admin/perfil" onClick={() => setOpen(false)} className="sidebar-link" style={{ marginTop: 10, padding: "8px 0", borderLeft: "none" }} data-testid="nav-perfil">
            <UserCircle size={14} /> Perfil
          </Link>
          <button onClick={onLogout} className="sidebar-link" style={{ marginTop: 2, padding: "10px 0", borderLeft: "none", color: "#BF3437" }} data-testid="logout-button">
            <LogOut size={14} /> Cerrar sesión
          </button>
        </div>
      </aside>

      <main className="md:ml-[240px] flex-1" style={{ minHeight: "100vh", paddingTop: 16 }}>
        <div className="md:hidden" style={{ height: 50 }} />
        <PanicBanner />
        <OperationalInbox />
        <Outlet />
      </main>
    </div>
  );
}
